export const en = {
  nav: {
    services: "Services",
    solutions: "Solutions",
    about: "About",
    insights: "Insights",
    contact: "Contact",
    talkToUs: "Talk to Us",
    seeWhatWeBuild: "See What We Build",
  },
  trustTicker: {
    builtFor: "Built for",
    items: ["Retail & Ecommerce", "CPG", "Professional Services", "Franchise", "DTC Brands"],
  },
  offerCard: {
    learnMore: "Learn more",
  },
  cta: {
    talkToUs: "Talk to Us",
  },
  home: {
    heroLabel: "Agentic AI · Generative AI · Predictive AI · Data Platforms",
    heroTitle: "We build the AI systems your business\u00A0runs on.",
    heroSubtitle:
      "Production-grade AI, data infrastructure, and decision systems.\nDesigned, built, and deployed by one team.",
    executionGapLabel: "The execution gap",
    executionGapTitle: "Most AI fails in the last mile. The model is rarely the problem.",
    executionGapQuote:
      "Pilots that demoed well and never shipped. Dashboards nobody acts on. Six-figure budgets that produced slideware instead of operating systems.",
    executionGapP1:
      "The distance between AI ambition and operating value is an engineering, data architecture, and decision-logic problem. It is the work required to run a system inside a real business, against real data, real constraints, and real users.",
    executionGapP2:
      "We close that gap. Every engagement ends with a deployed system in operation.",
    whyUsLabel: "Why Vert AI",
    whyUsTitle: "One firm.\nScope through production.",
    whyUsSubtitle:
      "No handoff between the people who think and the people who build. The team that frames the opportunity is the team that deploys the system.",
    diff1Title: "One team, scope to deploy",
    diff1Desc:
      "Strategy and engineering are not separated. The team that defines the solution designs the architecture, writes the code, and puts the system into production.",
    diff2Title: "Technical depth, commercial judgment",
    diff2Desc:
      "Data science, ML engineering, and AI systems architecture grounded in unit economics and measurable outcomes. We build systems that move the business.",
    diff3Title: "Production systems, not prototypes",
    diff3Desc:
      "Every engagement ships a system that operates inside your business. Monitored, documented, and built to run unattended.",
    diff4Title: "Guaranteed deliverables",
    diff4Desc:
      "Every engagement carries a defined output commitment. If the agreed scope is not delivered, we continue at zero professional fee until it is.",
    engagementsLabel: "Engagements",
    engagementsTitle: "Services",
    engagementsSubtitle:
      "Scoped to your business opportunity. Priced against expected value. Defined deliverables. Output guarantee included.",
    offer1Title: "Agentic AI",
    offer1Desc:
      "Automate the high-value workflows that consume your operating capacity. Bounded agents with routing, controls, fallback paths, and human-in-the-loop oversight, deployed end to end.",
    offer2Title: "Generative AI",
    offer2Desc:
      "Turn institutional knowledge and unstructured documents into reliable answers. Retrieval-grounded systems with source attribution, evaluation, and accuracy thresholds your teams can rely on.",
    offer3Title: "Predictive AI",
    offer3Desc:
      "Quantify what drives revenue, retention, and margin. Production models for churn, LTV, demand, recommendation, propensity, and profitability, deployed into the workflows where decisions happen.",
    offer4Title: "Data Platforms",
    offer4Desc:
      "Build the data foundation your AI and analytics depend on. Source-of-truth architecture, semantic layer, quality controls, and AI-readiness instrumentation engineered for trust.",
    domainsLabel: "What we build",
    domainsTitle: "Systems we deploy.\n",
    domainsSubtitle:
      "Production AI, data, and decision systems for the workflows standard software cannot fully address.",
    domain1Label: "AI agents and workflow automation",
    domain1Desc:
      "Reduce manual throughput costs in operations, finance, and internal execution. Bounded agents with routing logic, controls, fallback paths, and human review at the points that matter.",
    domain2Label: "Customer support and service systems",
    domain2Desc:
      "Lift resolution speed and contain cost-per-ticket without compromising quality. Intent classification, knowledge-grounded responses, agent assist, and confidence-based escalation.",
    domain3Label: "Knowledge systems and enterprise retrieval",
    domain3Desc:
      "Recover the hours your teams lose searching for answers. Retrieval-augmented assistants that reason across policies, SOPs, product data, and commercial documentation with source attribution.",
    domain4Label: "Document intelligence",
    domain4Desc:
      "Convert unstructured documents into reliable structured data. Extraction, classification, validation, and confidence scoring across contracts, proposals, invoices, and RFPs.",
    domain5Label: "Predictive models and decision science",
    domain5Desc:
      "Move commercial decisions from instinct to scored signal. Production models for churn, LTV, lead scoring, demand forecasting, and prioritization, deployed into the workflows where action happens.",
    domain6Label: "Recommendation and personalization",
    domain6Desc:
      "Lift conversion, basket size, and lifecycle revenue. Recommendation engines and next-best-action logic for ecommerce, merchandising, and customer experience workflows.",
    domain7Label: "Profitability and growth decision systems",
    domain7Desc:
      "Allocate capital against profit truth, not platform metrics. Measurement and modeling systems that connect channel, cohort, contribution margin, and payback economics for leadership.",
    domain8Label: "Data platforms for AI",
    domain8Desc:
      "Give every model and dashboard the same version of the truth. Source-of-truth architecture, semantic layer, QA, and instrumentation engineered for trust and traceability.",
    domain9Label: "AI operations, evaluation, and control",
    domain9Desc:
      "Keep production AI safe, observable, and improving. Monitoring, evaluation harnesses, prompt standards, human-in-the-loop controls, escalation logic, and operational safeguards.",
    founderLabel: "Founder",
    founderName: "Guillaume Belisle",
    founderRole: "Founder, Vert AI",
    founderP1:
      "Data scientist, ML engineer, and AI systems builder. Guillaume has designed and shipped production AI, ML, and data systems for Louis Vuitton, Canada Goose, Lowe's, Home Hardware, and other operators where a failed implementation carries real financial cost.",
    founderP2:
      "He built Vert AI because the industry separates the people who think from the people who build. We do not. The person who frames the problem also architects the system, writes the code, and ships it.",
    founderP3: "One point of contact. No layers. No subcontractors.",
    founderTags: ["AI systems", "Data engineering", "ML / DS", "Decision systems", "Profitability measurement"],
    insightsLabel: "Insights",
    insightsTitle: "Thinking that ships.",
    allInsights: "All insights",
    insightRead: "Read",
    insight1Cat: "AI Systems",
    insight1Title: "95% of enterprise AI projects fail in production. The problem was never the model.",
    insight1Desc:
      "Gartner, MIT, and every enterprise survey confirm it. The bottleneck is engineering, data architecture, and operational readiness, not model choice.",
    insight2Cat: "Data Engineering",
    insight2Title: "The semantic layer is the most undervalued asset in enterprise AI.",
    insight2Desc:
      "Only 7% of enterprises operate a governed semantic foundation. Without one, every model and every dashboard trains on a different version of the truth.",
    insight3Cat: "Decision Science",
    insight3Title: "Your growth metrics are lying. Here is what to measure instead.",
    insight3Desc:
      "Revenue is vanity. ROAS is a platform fiction. CAC ignores cohort decay. Most measurement frameworks quietly reward capital destruction.",
    faqLabel: "FAQ",
    faqTitle: "Straight answers.",
    faq1Q: "Strategy or implementation?",
    faq1A:
      "Both, by design. We frame the problem, architect the system, build it, and deploy it. One team, end to end. No handoff between the people who think and the people who build.",
    faq2Q: "What does production-grade mean?",
    faq2A:
      "The system runs inside your business. Not in a notebook. Not in a demo environment. Deployed, monitored, documented, and handed off with operating procedures and ownership.",
    faq3Q: "How is Vert AI different from other AI firms?",
    faq3A:
      "Technical depth and commercial judgment in one team. We do not build technology for its own sake. We build systems designed around measurable business outcomes, with guaranteed deliverables.",
    faq4Q: "Who do you work with?",
    faq4A:
      "CEOs, COOs, CFOs, and data leaders at mid-market companies. Retail, ecommerce, professional services, franchise operators, and growth-stage businesses. Operators who want deployed systems, not AI theater.",
    faq5Q: "How are engagements priced?",
    faq5A:
      "Scoped to the problem. Priced against expected value. Every engagement has defined deliverables, a fixed timeline, and a concrete output guarantee. No retainers. No open-ended statements of work.",
    faq6Q: "Do you offer post-launch support?",
    faq6A:
      "Yes. The Operating Cadence covers refinement, retraining, evaluation, performance tuning, and roadmap expansion on a recurring basis.",
    ctaHeadline: "The next step is a conversation.",
    ctaDescription:
      "Describe the problem. We will tell you what we would build, how long it takes, and what it costs. No decks. No discovery phases. A direct conversation.",
  },
  services: {
    heroLabel: "Services",
    heroTitle: "Four pillars. Production systems.",
    heroSubtitle:
      "Every engagement targets one business problem inside one of four AI pillars. Defined deliverables. Output guarantee. One team from scope to production.",
    coreLabel: "The four pillars",
    offer1Title: "Agentic AI",
    offer1Desc:
      "Bounded autonomous systems for operations, support, finance, and internal execution. Routing, controls, fallback paths, and human review at the points that matter.",
    offer2Title: "Generative AI",
    offer2Desc:
      "Retrieval-augmented systems that reason over institutional knowledge and unstructured documents. Grounded outputs, source attribution, and production-grade accuracy.",
    offer3Title: "Predictive AI",
    offer3Desc:
      "Production models that score, rank, forecast, and quantify economic truth. Churn, LTV, demand, recommendation, propensity, and profitability deployed into workflows.",
    offer4Title: "Data Platforms",
    offer4Desc:
      "The architecture, semantic layer, quality controls, and AI-readiness foundation required before AI, analytics, or decision systems can operate reliably.",
    howToChooseLabel: "How to choose",
    howToChooseTitle: "Start with the problem.",
    choose1Offer: "Agentic AI",
    choose1Situation:
      "A high-value workflow is consuming your operating capacity, and you need a partner who can scope it, build the agent system, and deploy it with the right controls and human-in-the-loop design.",
    choose1Start: "Start here for autonomous workflows.",
    choose2Offer: "Generative AI",
    choose2Situation:
      "Your teams are losing hours inside policies, SOPs, contracts, and documents. You need an AI system that reasons over institutional knowledge and returns grounded, attributable answers your teams can act on.",
    choose2Start: "Start here for knowledge and documents.",
    choose3Offer: "Predictive AI",
    choose3Situation:
      "Commercial decisions are being made on instinct. You need scoring, forecasting, recommendation, or profitability models deployed into the workflows where churn, LTV, demand, and capital allocation are decided.",
    choose3Start: "Start here for predictive models.",
    choose4Offer: "Data Platforms",
    choose4Situation:
      "Reports give different answers depending on who runs them. AI initiatives stall because the foundation is fragmented, inconsistent, or untrusted. The data layer needs to be rebuilt for trust.",
    choose4Start: "Start here for data infrastructure.",
    processLabel: "How we work",
    processTitle: "From problem to production.",
    step1Title: "Scope",
    step1Desc:
      "Define the problem. Identify the right pillar. Agree on deliverables, success criteria, and timeline.",
    step2Title: "Architect",
    step2Desc: "System design, data flow, integration plan, and deployment path. Before a line of code is written.",
    step3Title: "Build",
    step3Desc:
      "Implementation against defined checkpoints. Data work, model and system development, evaluation, quality assurance, and iteration.",
    step4Title: "Deploy",
    step4Desc: "Production rollout, documentation, team enablement, monitoring setup, and operational handoff.",
    domainsLabel: "Implementation domains",
    domainsTitle: "Solutions these engagements deliver.",
    domainsSubtitle:
      "Each pillar builds specific AI, data, and decision systems inside the business. These are the implementation domains we operate in.",
    domainsGroup1: "Agentic AI",
    domainsGroup2: "Generative AI",
    domainsGroup3: "Predictive AI",
    domainsGroup4: "Data Platforms",
    domainsGroupCross: "Cross-cutting · Post-launch",
    domain1: "AI agents and workflow automation",
    domain2: "Customer support and service systems",
    domain3: "Knowledge systems and enterprise retrieval",
    domain4: "Document intelligence",
    domain5: "Predictive models and decision science",
    domain6: "Recommendation and personalization",
    domain7: "Profitability and growth decision systems",
    domain8: "Data platforms for AI",
    domain9: "AI operations, evaluation, and control",
    supportLabel: "After the build",
    supportTitle: "Ongoing support.",
    support1Title: "AI Operator Layer",
    support1Desc:
      "The controls that keep AI systems usable, monitored, and safe in production. Access controls, human-in-the-loop design, evaluation rules, escalation logic, monitoring, and operational SOPs.",
    support2Title: "Operating Cadence",
    support2Desc:
      "Post-launch refinement, retraining, evaluation, performance tuning, and roadmap expansion. Available in Core, Plus, and Elite tiers with monthly reviews.",
    ctaHeadline: "Tell us the problem.",
    ctaDesc:
      "No deck required. No commitment. Describe the problem you need solved and we will tell you exactly what we would build.",
  },
  agenticAI: {
    backLink: "← Services",
    duration: "4–14 weeks",
    heroTitle: "Agentic AI",
    heroSubtitle:
      "Automate the high-value workflows that consume operating capacity. Bounded agent systems with routing, controls, fallback paths, and human review, shipped end to end.",
    heroTimeline: "Focused agent: 4–6 weeks · Integrated workflow: 6–10 weeks · Multi-agent system: 10–14 weeks",
    problemLabel: "The problem",
    problemP1:
      "Most agent demos collapse on first contact with real systems, real exceptions, and real accountability. The gap between a sandbox agent and one that operates safely inside a business is engineering, governance, and human-in-the-loop design, not model capability.",
    problemP2: "This engagement closes that gap. Bounded autonomy. Real controls. Production deployment. Guaranteed output.",
    buyersLabel: "Built for",
    buyers: [
      "COO or Head of Operations whose manual workflows do not scale with growth",
      "CFO or finance leader automating routing, validation, and exception handling",
      "Head of Customer Operations who needs intelligent triage and resolution at volume",
      "Function leader moving from agent demos to a deployed, governed system",
    ],
    processLabel: "How it works",
    processTitle: "Six phases to production.",
    phases: [
      {
        num: "01",
        title: "Scope the workflow and frame the economics",
        desc: "Workflow selection, autonomy boundary definition, economic justification, success criteria, and ownership mapping. Output: a one-page workflow brief and acceptance criteria.",
      },
      {
        num: "02",
        title: "Design the agent architecture",
        desc: "End-to-end system design covering routing logic, tool and integration selection, control boundaries, fallback paths, and human-in-the-loop checkpoints. Output: an architecture diagram and tool inventory.",
      },
      {
        num: "03",
        title: "Build the orchestration",
        desc: "Tool wiring, prompt and orchestration design, business rules, exception handling, escalation logic, and operational guardrails. Output: a working agent against the agreed scope.",
      },
      {
        num: "04",
        title: "Evaluate and harden",
        desc: "Test cases, failure-mode analysis, behavioral scoring, business acceptance criteria, and reliability thresholds for autonomous behavior. Output: an evaluation report and reliability sign-off.",
      },
      {
        num: "05",
        title: "Deploy to production",
        desc: "Production rollout, access and permissions, monitoring requirements, audit trails, operational handoff, and full documentation. Output: a live system with monitoring in place.",
      },
      {
        num: "06",
        title: "Govern and adopt",
        desc: "User guidance, operating SOPs, escalation playbooks, and governance rules for safe, ongoing autonomous operation. Output: an adoption playbook and governance framework.",
      },
    ],
    useCasesLabel: "Common use cases",
    useCases: [
      "Operational workflow automation",
      "Customer support triage and resolution",
      "Internal request routing",
      "Finance and procurement automation",
      "Sales and operations copilots",
      "Compliance checks and validation",
      "Multi-step orchestration with human review",
      "Rules-and-LLM hybrid workflows",
      "Exception handling and escalation",
      "Bounded multi-agent systems",
    ],
    guaranteeLabel: "Guarantee",
    guaranteeTitle: "Production Agent Guarantee",
    guaranteeDesc:
      "If we do not deliver a complete agent design, a working implementation for the agreed scope, and a clear deployment path with operational controls, we keep working at zero professional fee until we do.",
    ctaHeadline: "Describe the workflow.",
    ctaDesc: "We scope the agent system, the controls, and the deployment path.",
  },
  generativeAI: {
    backLink: "← Services",
    duration: "4–12 weeks",
    heroTitle: "Generative AI",
    heroSubtitle:
      "Turn institutional knowledge and unstructured documents into reliable answers. Retrieval-grounded systems with citations, evaluation harnesses, and accuracy thresholds engineered for production.",
    heroTimeline: "Focused build: 4–6 weeks · Multi-source: 6–9 weeks · Enterprise deployment: 9–12 weeks",
    problemLabel: "The problem",
    problemP1:
      "Generative AI without grounding is a liability. Hallucinated answers, missing citations, and unreliable extraction have killed more enterprise pilots than any technical limitation. The systems that work anchor every answer to a verifiable source and are evaluated against business accuracy standards, not generic benchmarks.",
    problemP2: "This engagement builds those systems. Retrieval-grounded. Source-attributed. Production-evaluated.",
    buyersLabel: "Built for",
    buyers: [
      "Head of Operations whose teams lose hours inside policies, SOPs, and product data",
      "Legal, finance, or procurement leader processing high volumes of contracts and documents",
      "Head of Customer Experience who needs grounded, attributable assistance for agents",
      "Knowledge or learning leader building internal assistants for staff",
    ],
    processLabel: "How it works",
    processTitle: "Six phases to grounded systems.",
    phases: [
      {
        num: "01",
        title: "Scope the use case and map the sources",
        desc: "Knowledge or document scope defined, source inventory, accuracy targets, retrieval strategy, and ownership mapping. Output: a source map and accuracy specification.",
      },
      {
        num: "02",
        title: "Design the retrieval architecture",
        desc: "Ingestion design, chunking and embedding strategy, retrieval and reranking logic, grounding rules, and source-attribution model. Output: a retrieval architecture and evaluation plan.",
      },
      {
        num: "03",
        title: "Design generation and reasoning",
        desc: "Prompt and reasoning structure, citation enforcement, refusal and uncertainty handling, output schemas, and validation logic. Output: a working generation layer with citation enforcement.",
      },
      {
        num: "04",
        title: "Evaluate accuracy and citations",
        desc: "Gold-set evaluation, accuracy and citation scoring, failure-mode analysis, business acceptance thresholds, and continuous-feedback design. Output: an evaluation harness and accuracy sign-off.",
      },
      {
        num: "05",
        title: "Deploy to production",
        desc: "Production rollout, access controls, monitoring for drift and accuracy, operational handoff, and full documentation. Output: a live system with drift monitoring in place.",
      },
      {
        num: "06",
        title: "Adopt and improve continuously",
        desc: "User guidance, content-gap reporting, feedback loops, and a maintenance cadence for the underlying knowledge sources. Output: an improvement loop and content-ownership model.",
      },
    ],
    useCasesLabel: "Common use cases",
    useCases: [
      "Internal knowledge assistants",
      "Policy and SOP retrieval",
      "Contract and clause extraction",
      "Invoice and document processing",
      "RFP response generation",
      "Sales and product knowledge assistants",
      "Compliance documentation review",
      "Technical support knowledge systems",
      "Multi-source enterprise search",
      "Conversational interfaces over private data",
    ],
    guaranteeLabel: "Guarantee",
    guaranteeTitle: "Grounded Output Guarantee",
    guaranteeDesc:
      "If we do not deliver a working retrieval and generation system that meets the agreed accuracy and citation standards, we keep working at zero professional fee until we do.",
    ctaHeadline: "Show us the knowledge problem.",
    ctaDesc: "We scope the retrieval system, the accuracy targets, and the deployment path.",
  },
  predictiveAI: {
    backLink: "← Services",
    duration: "4–14 weeks",
    heroTitle: "Predictive AI",
    heroSubtitle:
      "Quantify what drives revenue, retention, and margin, and put that signal where decisions happen. Production models for churn, LTV, demand, recommendation, and profitability, shipped end to end.",
    heroTimeline: "Focused model: 4–6 weeks · Multi-model system: 6–10 weeks · Production deployment: 10–14 weeks",
    problemLabel: "The problem",
    problemP1:
      "Most analytics stays descriptive. Companies invest in reporting and never build the predictive systems that improve the decisions driving revenue, margin, and growth. The models that exist sit in notebooks, never reach the workflows where decisions happen, and are never evaluated against business outcomes.",
    problemP2: "This engagement builds models that drive action. Scored, deployed, and connected to the decisions that compound profit.",
    buyersLabel: "Built for",
    buyers: [
      "CEO or Founder who needs sharper commercial intelligence to guide capital and growth decisions",
      "CFO or CMO sizing churn, LTV, payback, and channel-mix decisions",
      "VP Growth or Head of Data Science who needs production-grade models, not notebooks",
      "Merchandising, ecommerce, or operations leader optimizing high-volume decisions",
    ],
    processLabel: "How it works",
    processTitle: "Six phases to decision utility.",
    phases: [
      {
        num: "01",
        title: "Frame the decision and the economics",
        desc: "Target decision defined, success metric established, economic value framed, and use case selected. Includes profitability and capital-allocation framings where relevant. Output: a one-page decision brief.",
      },
      {
        num: "02",
        title: "Design the model and features",
        desc: "Model approach selection, feature engineering, label design, validation strategy, and accuracy-versus-usability tradeoffs. Output: a feature spec and validation plan.",
      },
      {
        num: "03",
        title: "Train and evaluate",
        desc: "Train and validation framework, performance evaluation, business relevance checks, threshold and segmentation logic, and error analysis. Output: a validated model with documented performance.",
      },
      {
        num: "04",
        title: "Design the deployment",
        desc: "Scoring logic, production usage pattern, handoff into workflow or decision layer, retraining schedule, and monitoring. Output: a deployment design with retraining cadence.",
      },
      {
        num: "05",
        title: "Translate into business interpretation",
        desc: "Score meaning, action framework, downstream usage guidance, caveats, and reliability notes for operators. Output: a score-to-action playbook.",
      },
      {
        num: "06",
        title: "Integrate into the decision layer",
        desc: "Connection to the workflow, dashboard, or playbook where the score is acted on. For profitability work, this includes cut, scale, or fix recommendation logic. Output: a live decision system in the workflow.",
      },
    ],
    useCasesLabel: "Common use cases",
    useCases: [
      "Churn prediction and intervention",
      "Customer lifetime value modeling",
      "Lead and account scoring",
      "Demand and inventory forecasting",
      "Recommendation and next-best-action",
      "Propensity and segmentation models",
      "Pricing and assortment support",
      "Profitability and cohort economics",
      "Channel and payback measurement",
      "Operational classification and prioritization",
    ],
    guaranteeLabel: "Guarantee",
    guaranteeTitle: "Decision Utility Guarantee",
    guaranteeDesc:
      "If we do not deliver a validated model or scoring system, a clear business interpretation, and a deployment design that connects the output to a real decision, we keep working at zero professional fee until we do.",
    ctaHeadline: "Name the decision.",
    ctaDesc: "We will scope the model, the data requirements, and the deployment path.",
  },
  dataPlatforms: {
    backLink: "← Services",
    duration: "4–14 weeks",
    heroTitle: "Data Platforms",
    heroSubtitle:
      "The data foundation your AI and analytics depend on. Source-of-truth architecture, semantic layer, quality controls, and AI-readiness instrumentation engineered for trust and traceability.",
    heroTimeline: "Focused: 4–6 weeks · Multi-source: 6–10 weeks · Broader environment: 10–14 weeks",
    problemLabel: "The problem",
    problemP1:
      "AI and analytics investments collapse when the data underneath is fragmented or untrusted. Models trained on unreliable data produce unreliable decisions. Generative systems retrieve the wrong context. Predictive models train on broken features. Reports give different answers depending on who runs them.",
    problemP2: "This engagement builds the trust layer your AI, analytics, and decision stack depend on.",
    buyersLabel: "Built for",
    buyers: [
      "COO or CFO who needs trustworthy reporting and decision-grade data",
      "Head of Data or analytics leader accountable for data quality and reliability",
      "RevOps or Marketing Ops leader with measurement gaps they cannot close",
      "Technical owner preparing data infrastructure for AI, ML, or generative deployment",
    ],
    processLabel: "How it works",
    processTitle: "Six phases to decision-grade data.",
    phases: [
      {
        num: "01",
        title: "Map the architecture and the sources",
        desc: "Source-system inventory, target architecture, source-of-truth design, entity mapping, and ownership structure. Output: a source map and target architecture diagram.",
      },
      {
        num: "02",
        title: "Model the data and the semantic layer",
        desc: "Core data models, semantic logic, metric definitions, transformation plan, and business-rule implementation. Output: implemented data models and a metric dictionary.",
      },
      {
        num: "03",
        title: "Implement quality and trust controls",
        desc: "QA checks, reconciliation rules, taxonomy cleanup, identity and duplication logic, and trust-gap remediation. Output: a quality test suite and reconciliation reports.",
      },
      {
        num: "04",
        title: "Instrument the event layer",
        desc: "Event layer design, process-stage capture, handoff tracking, exception logging, and traceability. Output: an event schema and instrumentation in production.",
      },
      {
        num: "05",
        title: "Prepare the AI and ML readiness layer",
        desc: "Feature availability, retrieval-readiness for generative systems, decision-grade vs. directional-grade classification, and upstream blocker identification. Output: an AI-readiness assessment and feature catalog.",
      },
      {
        num: "06",
        title: "Document and hand off",
        desc: "KPI dictionary, logic documentation, architecture notes, maintenance guidance, and roadmap for downstream AI systems. Output: a documentation set and downstream roadmap.",
      },
    ],
    useCasesLabel: "Common problems we solve",
    useCases: [
      "Disconnected source systems",
      "Inconsistent KPI logic across teams",
      "Weak source-of-truth structure",
      "Poor feature availability for ML and AI",
      "Broken reporting trust",
      "Missing instrumentation and event capture",
      "Unreliable input data for models",
      "No production-ready data models",
      "Retrieval gaps for generative systems",
    ],
    guaranteeLabel: "Guarantee",
    guaranteeTitle: "Decision-Grade Data Guarantee",
    guaranteeDesc:
      "If the agreed scope does not result in a documented source-of-truth structure, implemented core data logic, and a usable trust and QA layer, we keep working at zero professional fee until it does.",
    ctaHeadline: "Start with the data.",
    ctaDesc: "Describe your data landscape. We scope the architecture and the path to reliability.",
  },
  about: {
    heroLabel: "About Vert AI",
    heroTitle: "We exist because most firms cannot build what they recommend.",
    heroSubtitle:
      "Vert AI is a Canadian AI and data engineering firm. We design, build, and deploy production systems for operators who measure success in deployed capability, not slideware.",
    whyExistsLabel: "Why Vert exists",
    whyExistsP1: "Most companies do not need more AI theater. They need production systems that improve how work gets done and how decisions get made.",
    whyExistsP2: "The market is crowded with strategy decks, demos, and pilots that never become operating capability. The result is six-figure spend, no system in production, and a leadership team that loses confidence in AI entirely.",
    whyExistsP3: "Vert AI exists to close that gap. We are the implementation partner for operators who are past the inspiration phase and need something that actually runs.",
    refuseLabel: "What we refuse to do",
    refuseItems: [
      "AI strategy decks with no build path",
      "Discovery phases priced like engagements",
      "Generic chatbots without a real workflow",
      "Dashboards that do not change a decision",
      "Pilots designed to look good in demo, not survive production",
      "Pretending AI is the answer when it is not",
    ],
    builtForLabel: "What we are built for",
    builtForIntro: "Talk to us when the problem is real enough to build around.",
    builtForFit: [
      "A workflow that is expensive, slow, inconsistent, or hard to scale",
      "Data that exists but does not drive enough decisions",
      "Dashboards leadership still debates instead of acting on",
      "A need for forecasting, scoring, routing, measurement, or decision support",
      "AI tools that were bought but never fit the workflow",
      "A system people need to trust in production, not just demo",
    ],
    notFitLabel: "Not a fit",
    notFitItems: [
      "Looking for AI inspiration with no committed problem",
      "Want a generic chatbot with no defined workflow",
      "Want a strategy deck with no implementation path",
      "Not ready to expose the real business problem",
    ],
    beliefsLabel: "What we believe",
    beliefs: [
      "Execution is the bottleneck. Not AI.",
      "Strategy without engineering is noise.",
      "Better systems produce better decisions.",
      "Data trust precedes data scale.",
      "A deployed system is the only deliverable that matters.",
    ],
    approachLabel: "Approach",
    approachTitle: "Technical depth.\nCommercial clarity.\nProduction focus.",
    approachP1:
      "We work with leadership teams on the problems that determine whether AI and data investments produce measurable value or become expensive overhead.",
    approachP2:
      "Every engagement starts with the business case, not the technology. We design around operating gains, unit economics, and outcomes you can measure. We scope, architect, build, and deploy. One team, end to end.",
    approachP3: "Structured engagements. Defined deliverables. Guaranteed outputs. No retainers. No ambiguity.",
    founderLabel: "Founder",
    founderName: "Guillaume Belisle",
    founderRole: "Founder, Vert AI",
    founderP1:
      "Data scientist, ML engineer, and AI systems builder. Guillaume has designed and shipped production AI, ML, and data systems for Louis Vuitton, Canada Goose, Lowe's, Home Hardware, and other operators where a failed implementation carries real financial cost.",
    founderP2:
      "He built Vert AI because the industry separates the people who think from the people who build. We do not. The person who frames the problem also architects the system, writes the code, and ships it.",
    founderP3: "One point of contact. No layers. No subcontractors.",
    founderTags: ["AI systems", "Data engineering", "ML / DS", "Decision systems", "Profitability measurement"],
    ctaHeadline: "Bring us the problem.",
    ctaDesc: "We scope the system, the timeline, and the deliverables.",
  },
  insights: {
    heroLabel: "Insights",
    heroTitle: "Thinking that ships.",
    heroSubtitle: "Implementation-grade analysis on AI systems, data infrastructure, and the economics of commercial decision-making.",
    featured: "Featured",
    readArticle: "Read article",
    articles: [
      {
        cat: "AI Strategy",
        title: "The AI procurement trap: how 2026 vendor contracts mortgage your roadmap.",
        desc: "Four structural clauses in standard enterprise AI contracts look reasonable in legal review and become catastrophic in year two: egress fees, model-version lock, embedding portability, and AI feature bundling. The procurement checklist a senior operator should run before signing.",
        date: "May 2026",
        readTime: "9 min read",
        keywords: "AI vendor contract, AI vendor lock-in, enterprise AI procurement, AI MSA red flags, AI contract clauses, foundation model contracts, AI vendor evaluation, AI egress fees",
        tags: ["AI Procurement", "Vendor Strategy", "Enterprise AI", "AI Contracts", "Vendor Lock-in"],
      },
      {
        cat: "AI Operations",
        title: "Human-in-the-loop is not a safety feature. It is a P&L line.",
        desc: "Every AI-augmented workflow is a labor reallocation. The economics only work when review time, escalation rate, and confidence-threshold calibration are designed together. A three-variable HITL cost model, and where teams burn the productivity gain on review queues.",
        date: "April 2026",
        readTime: "8 min read",
        keywords: "human in the loop AI, AI review workflow, AI operations cost, HITL economics, AI confidence threshold, AI escalation rate, AI augmented workflow, AI labor model",
        tags: ["AI Operations", "Human in the Loop", "AI Economics", "Workflow Design", "Production AI"],
      },
      {
        cat: "AI Systems",
        title: "The evaluation gap: why most enterprise AI systems have no honest scorecard.",
        desc: "F1, BLEU, and user satisfaction are not commercial metrics. The four-layer evaluation architecture, offline, shadow, controlled rollout, and in-production drift, that separates demos from systems leadership can defend at the board level.",
        date: "April 2026",
        readTime: "8 min read",
        keywords: "AI evaluation framework, enterprise AI metrics, model monitoring, AI scorecard, shadow evaluation, AI drift detection, production AI evaluation, LLM evaluation",
        tags: ["AI Evaluation", "Enterprise AI", "Model Monitoring", "AI Operations", "Production AI"],
      },
      {
        cat: "AI Systems",
        title: "Build vs buy for AI has changed. Here is the 2026 decision framework.",
        desc: "Foundation models are commodities. Orchestration is the differentiator. A three-axis decision model, proprietary data leverage, workflow specificity, and defensibility horizon, for choosing between SaaS AI features, assembled stacks, and bespoke systems.",
        date: "March 2026",
        readTime: "7 min read",
        keywords: "build vs buy AI, enterprise AI strategy, AI decision framework, foundation models, AI stack assembly, proprietary AI, AI vendor evaluation, AI defensibility",
        tags: ["AI Strategy", "Build vs Buy", "Enterprise AI", "AI Architecture", "Vendor Selection"],
      },
      {
        cat: "AI Systems",
        title: "95% of enterprise AI projects fail in production. The problem was never the model.",
        desc: "Gartner, MIT, and every enterprise survey confirm it. The bottleneck is engineering, data architecture, and operational readiness, not model selection. What implementation-led teams do differently.",
        date: "March 2026",
        readTime: "8 min read",
      },
      {
        cat: "Data Engineering",
        title: "The semantic layer is the most undervalued asset in enterprise AI.",
        desc: "Only 7% of enterprises operate a governed semantic foundation. Without one, every model and dashboard trains on a different version of the truth. Why the semantic layer is the highest-ROI infrastructure investment most data teams skip.",
        date: "February 2026",
        readTime: "7 min read",
      },
      {
        cat: "Decision Science",
        title: "Your growth metrics are lying. Here is what to measure instead.",
        desc: "Revenue is vanity. ROAS is a platform fiction. CAC ignores cohort decay. Most growth measurement frameworks quietly reward capital destruction. A decision-science approach to measuring what actually compounds.",
        date: "January 2026",
        readTime: "6 min read",
      },
      {
        cat: "AI Systems",
        title: "Agentic AI in production: why most enterprise deployments will fail by 2027.",
        desc: "Gartner predicts 40% of enterprise apps will use agentic AI by end of 2026. Most will not survive contact with production data. The governance, evaluation, and operational gaps that separate demos from deployed systems.",
        date: "December 2025",
        readTime: "9 min read",
      },
      {
        cat: "Data Engineering",
        title: "Your data is not ready for AI. Five diagnostic signals.",
        desc: "Before investing in models, run this diagnostic. Five structural signals, from pipeline fragility to semantic drift, that predict whether your data layer will enable AI or undermine it.",
        date: "November 2025",
        readTime: "5 min read",
      },
      {
        cat: "Profitability Intelligence",
        title: "The hidden cost layer: why AI ROI calculations miss 40–60% of real spend.",
        desc: "Change management, data preparation, integration work, and operational overhead account for up to 60% of total AI investment. Almost none of it appears in the business case. How to build an honest cost model.",
        date: "October 2025",
        readTime: "7 min read",
      },
      {
        cat: "AI Systems",
        title: "The gap between a trained model and a production system is an engineering problem.",
        desc: "A model in a notebook is not a system. Monitoring, versioning, fallback logic, and operational scaffolding separate a demo from a deployed capability, and most ML teams are not equipped to close that gap.",
        date: "September 2025",
        readTime: "6 min read",
      },
      {
        cat: "Decision Science",
        title: "Decision layers: the missing architecture between data and action.",
        desc: "Most companies have dashboards. Very few have decision systems. The architectural pattern that connects predictive models, business rules, and operational workflows into systems that generate action, not reports.",
        date: "August 2025",
        readTime: "7 min read",
      },
      {
        cat: "Data Engineering",
        title: "From ETL to AI-native: what the 2026 data stack actually looks like.",
        desc: "The modern data stack is dead. The AI-native data stack, real-time, context-rich, and semantically governed, is what production AI requires. An architectural blueprint for data teams building for agentic systems.",
        date: "July 2025",
        readTime: "8 min read",
      },
      {
        cat: "Profitability Intelligence",
        title: "Capital allocation is a data problem. Most companies solve it with opinions.",
        desc: "Where to spend, where to cut, where to scale. These are measurement questions, not strategy questions. Companies that treat capital allocation as a data engineering problem outperform those that do not.",
        date: "June 2025",
        readTime: "6 min read",
      },
    ],
  },
  contact: {
    heroLabel: "Contact",
    heroTitle: "Bring us the problem.",
    heroSubtitle: "Tell us the workflow, decision, or data problem you want to solve. We respond within one business day with a practical view of what we would build, what it would take, and whether we are the right fit.",
    howToReach: "How to reach us",
    scopingCall: "Send us your inquiry.",
    scopingCallDesc:
      "Describe the workflow, the data challenge, or the decision you want to improve. We read every submission directly and respond with an honest assessment of fit, scope, and the right next step. No decks. No discovery theater.",
    emailLabel: "Email",
    locationLabel: "Location",
    location: "Montreal, Canada",
    responseTimeLabel: "Response time",
    responseTime: "Within one business day",
    formName: "Name",
    formNamePlaceholder: "Your name",
    formCompany: "Company",
    formCompanyPlaceholder: "Company name",
    formEmail: "Email",
    formEmailPlaceholder: "you@company.com",
    formMessage: "What problem are you trying to solve?",
    formMessagePlaceholder:
      "Describe the workflow, the data challenge, or the decision you need to improve. The more specific, the more useful the response.",
    formImpact: "What happens if you do not fix it?",
    formImpactPlaceholder: "Cost, risk, lost capacity, slower decisions, missed revenue. Optional, but it sharpens our response.",
    formTimeline: "Timeline or urgency",
    formTimelinePlaceholder: "Now, this quarter, exploratory. Optional.",
    formStack: "Current tools or data stack",
    formStackPlaceholder: "Optional. Helps us scope integration realistically.",
    formOptional: "Optional",
    formSubmit: "Send Message",
  },
  notFound: {
    title: "404",
    message: "Page not found.",
    backHome: "Return to Home",
  },
  footer: {
    tagline: "Production-grade AI, data, and decision systems. Designed and shipped from Canada.",
    engagements: "Engagements",
    company: "Company",
    contactLabel: "Contact",
    copyright: "Vert AI Inc.",
    bottomTagline: "AI, Data & Decision Systems · Canada",
  },
  solutionsHub: {
    heroLabel: "Solutions",
    heroTitle: "Systems we build.\nProblems we solve.",
    heroSubtitle: "The categories of AI, data, and decision systems we design, build, and ship.",
    primaryLabel: "Solution categories",
    primaryTitle: "Explore by problem.",
    primarySubtitle: "Each category is a distinct class of system we build. Open one to see the problem, what we deliver, and which engagement fits.",
    secondaryLabel: "Additional capabilities",
    secondaryTitle: "Supporting domains.",
    recommendationTitle: "Recommendation and personalization",
    recommendationDesc: "Recommendation engines and next-best-action logic for ecommerce, merchandising, and customer experience.",
    recommendationVia: "Via Predictive AI",
    profitabilityGrowthTitle: "Profitability and growth decisions",
    profitabilityGrowthDesc: "Measurement and modeling systems connecting channel, cohort, and payback economics for capital allocation.",
    profitabilityGrowthVia: "Via Predictive AI",
    aiOpsTitle: "AI operations and control",
    aiOpsDesc: "Monitoring, QA, evaluation, escalation logic, and operational safeguards for production AI systems.",
    aiOpsVia: "Cross-cutting · Post-launch",
    bridgeLabel: "How to engage",
    bridgeTitle: "From problem to engagement.",
    bridgeDesc: "Every solution ships through one of our four engagements. Scoped to your problem, priced against expected value, output guarantee included.",
    bridgeCta: "View engagements",
    ctaHeadline: "Tell us the problem.",
    ctaDesc: "We tell you what we would build, which engagement fits, and what it costs.",
  },
  solutions: {
    aiAgents: {
      label: "Solution",
      heroTitle: "AI Agents and Workflow Automation",
      hubDesc: "Bounded AI workflows for operations, finance, and internal execution. With routing, controls, and human review.",
      heroSubtitle: "Bounded AI workflows that automate high-value, repetitive processes. Built with routing, controls, fallback paths, and human review at the points that matter.",
      problemLabel: "The problem",
      problemTitle: "Manual processes that do not scale.",
      problemP1: "High-value workflows in operations, finance, and internal execution are still run by hand. Volume is growing. Headcount is not. Generic automation handles the simple cases and breaks on exceptions, edge cases, and anything requiring judgment.",
      problemP2: "Vert AI builds AI agents that handle the routine, escalate the exceptions, and keep humans in control where it matters.",
      gapsLabel: "Why generic tools fall short",
      gaps: "Cannot handle multi-step reasoning or branching logic|No fallback or escalation when confidence drops|Cannot integrate across multiple internal systems|No audit trail or operational controls|Break on edge cases that require contextual judgment",
      buildsLabel: "What we build",
      buildsTitle: "Systems we deliver in this category.",
      builds: "Multi-step AI agents for operational workflows|Routing and triage systems with confidence scoring|Document-triggered automation pipelines|Finance and procurement workflow automation|Internal copilots for functional teams|Rules-and-LLM hybrid orchestration|Exception handling and escalation logic|Human-in-the-loop review interfaces",
      workflowsLabel: "Typical workflows",
      workflowsTitle: "Where this applies.",
      workflows: "Invoice processing and validation|Order management and exception handling|Compliance checking and document review|Employee onboarding automation|Internal request routing and triage|Supply chain communication automation|Audit preparation and evidence gathering",
      humanLabel: "Human oversight",
      humanTitle: "Humans stay in the loop.",
      humanDesc: "Every agent system includes configurable escalation thresholds, manual review queues, and operational controls. Automation handles the volume. Humans handle the judgment. The system knows the difference.",
      engagementLabel: "Typical engagement",
      engagementTitle: "Most often delivered through Agentic AI.",
      engagementDesc: "This solution is typically delivered through the Agentic AI engagement, which covers scoping, architecture, implementation, evaluation, and production deployment. One team, end to end.",
      engagementHref: "/services/agentic-ai",
      engagementCta: "Learn about Agentic AI",
      ctaHeadline: "Describe the workflow.",
      ctaDesc: "We will scope the system, the timeline, and the deliverables.",
    },
    documentIntelligence: {
      label: "Solution",
      heroTitle: "Document Intelligence",
      hubDesc: "Extraction, classification, and structured output from contracts, invoices, RFPs, and high-friction document workflows.",
      heroSubtitle: "Extraction, classification, validation, and structured output from contracts, proposals, invoices, RFPs, and other high-friction document workflows.",
      problemLabel: "The problem",
      problemTitle: "Documents that trap value.",
      problemP1: "Critical business information is locked inside unstructured documents. Contracts, proposals, invoices, and regulatory filings hold the data your systems need, but extracting it reliably at scale is a problem generic OCR and template-based tools cannot solve.",
      problemP2: "Vert AI builds document intelligence systems that extract, classify, validate, and structure information from complex documents with production-grade reliability.",
      gapsLabel: "Why generic tools fall short",
      gaps: "Template-based extraction breaks on format variation|OCR alone cannot handle semantic understanding|No validation or confidence scoring|Cannot map extracted data to business entities|No feedback loop for continuous improvement",
      buildsLabel: "What we build",
      buildsTitle: "Systems we deliver in this category.",
      builds: "Contract analysis and clause extraction|Invoice processing and validation systems|RFP response automation|Regulatory document classification|Insurance claims processing|Proposal comparison and scoring|Document-to-database pipelines|Multi-format extraction with confidence scoring",
      workflowsLabel: "Typical workflows",
      workflowsTitle: "Where this applies.",
      workflows: "Procurement contract review|Accounts payable automation|Legal document analysis|Insurance underwriting document processing|Compliance document classification|Proposal intake and scoring|Regulatory filing extraction",
      humanLabel: "Human oversight",
      humanTitle: "Humans validate what matters.",
      humanDesc: "Every document intelligence system includes confidence thresholds, manual review queues for low-confidence extractions, and validation rules aligned with business logic. The system handles volume. Humans handle exceptions and edge cases.",
      engagementLabel: "Typical engagement",
      engagementTitle: "Usually delivered through Generative AI.",
      engagementDesc: "Document intelligence solutions are typically delivered through the Generative AI engagement. For organizations that also need to restructure their data foundation, the Data Platforms engagement may apply.",
      engagementHref: "/services/generative-ai",
      engagementCta: "Learn about Generative AI",
      ctaHeadline: "Show us the documents.",
      ctaDesc: "We will scope the extraction system, the accuracy targets, and the deployment path.",
    },
    knowledgeSystems: {
      label: "Solution",
      heroTitle: "Knowledge Systems and Enterprise Retrieval",
      hubDesc: "Internal assistants that retrieve, reason over, and operationalize institutional knowledge across your organization.",
      heroSubtitle: "Internal assistants that retrieve, reason over, and operationalize institutional knowledge across policies, SOPs, product data, and commercial documentation.",
      problemLabel: "The problem",
      problemTitle: "Knowledge that exists but cannot be found.",
      problemP1: "Your organization has the answers. They are scattered across hundreds of documents, wikis, SOPs, and legacy systems. Employees lose hours searching for information that should take seconds. Generic search returns keywords, not answers.",
      problemP2: "Vert AI builds knowledge systems that understand questions, retrieve the relevant context, reason across sources, and deliver actionable answers with citations.",
      gapsLabel: "Why generic search falls short",
      gaps: "Keyword search returns results, not answers|Cannot reason across multiple documents|No understanding of organizational context|Cannot handle follow-up questions or clarification|No source attribution or confidence scoring",
      buildsLabel: "What we build",
      buildsTitle: "Systems we deliver in this category.",
      builds: "Enterprise knowledge assistants with RAG architecture|Policy and SOP retrieval systems|Product information retrieval for sales and support|Technical documentation assistants|Multi-source knowledge aggregation|Conversational interfaces with source attribution|Context-aware search with semantic understanding|Knowledge base curation and maintenance tooling",
      workflowsLabel: "Typical workflows",
      workflowsTitle: "Where this applies.",
      workflows: "HR policy and benefits questions|Technical support knowledge retrieval|Sales enablement and product information|Regulatory compliance knowledge access|Onboarding and training knowledge delivery|Operations procedure lookup|Cross-department knowledge sharing",
      humanLabel: "Human oversight",
      humanTitle: "Accuracy you can verify.",
      humanDesc: "Every knowledge system includes source attribution, confidence indicators, and feedback mechanisms. Users can verify where answers come from. Administrators can track accuracy, identify knowledge gaps, and improve the system continuously.",
      engagementLabel: "Typical engagement",
      engagementTitle: "Delivered through Generative AI.",
      engagementDesc: "Knowledge systems are delivered through the Generative AI engagement. The build covers retrieval architecture design, ingestion pipeline, reasoning logic, and production deployment with monitoring.",
      engagementHref: "/services/generative-ai",
      engagementCta: "Learn about Generative AI",
      ctaHeadline: "Describe the knowledge problem.",
      ctaDesc: "We will scope the retrieval system, the sources, and the deployment path.",
    },
    customerSupport: {
      label: "Solution",
      heroTitle: "Customer Support and Service Systems",
      hubDesc: "AI systems that resolve requests, assist agents, classify intent, and improve speed without sacrificing quality.",
      heroSubtitle: "AI systems that resolve repetitive requests, assist agents, classify intent, surface the right knowledge, and improve speed without sacrificing quality or oversight.",
      problemLabel: "The problem",
      problemTitle: "Support that does not scale.",
      problemP1: "Ticket volume grows. Agent capacity does not. Repetitive questions consume experienced agents. Response times drift. Quality becomes inconsistent. Generic chatbots handle only the simplest cases and frustrate customers on anything beyond FAQ-level queries.",
      problemP2: "Vert AI builds support systems that handle the routine with precision, assist agents on complex cases, and escalate when human judgment is required.",
      gapsLabel: "Why generic chatbots fall short",
      gaps: "Only handle FAQ-level questions|No integration with internal knowledge or systems|Cannot assist human agents on complex cases|No intent classification or routing logic|No learning from resolution patterns",
      buildsLabel: "What we build",
      buildsTitle: "Systems we deliver in this category.",
      builds: "AI-powered ticket triage and routing|Automated resolution for common request types|Agent-assist with suggested responses|Intent classification and priority scoring|Knowledge-grounded response generation|Confidence-based escalation logic|Customer sentiment and urgency detection|Resolution quality monitoring and feedback loops",
      workflowsLabel: "Typical workflows",
      workflowsTitle: "Where this applies.",
      workflows: "Tier-1 support automation|Order status and tracking inquiries|Returns and exchange processing|Account and billing questions|Product information requests|Service scheduling and coordination|Complaint classification and routing",
      humanLabel: "Human oversight",
      humanTitle: "Agents stay in control.",
      humanDesc: "Every support system includes configurable automation boundaries, agent override, and escalation thresholds. AI handles volume and consistency. Agents handle complexity and empathy. The system routes intelligently between the two.",
      engagementLabel: "Typical engagement",
      engagementTitle: "Delivered through Agentic AI.",
      engagementDesc: "Customer support systems are delivered through the Agentic AI engagement. Scope includes intent modeling, knowledge integration, agent-assist interfaces, and production deployment with quality monitoring.",
      engagementHref: "/services/agentic-ai",
      engagementCta: "Learn about Agentic AI",
      ctaHeadline: "Describe the support challenge.",
      ctaDesc: "We will scope the system, the automation boundaries, and the deployment path.",
    },
    predictiveModels: {
      label: "Solution",
      heroTitle: "Predictive Models and Decision Science",
      hubDesc: "Custom models for churn, LTV, scoring, and demand forecasting. Built to rank, score, and guide action in production.",
      heroSubtitle: "Custom models for churn, LTV, lead scoring, demand forecasting, prioritization, and commercial decision support. Built to score, rank, and guide action in production.",
      problemLabel: "The problem",
      problemTitle: "Decisions driven by instinct, not signal.",
      problemP1: "Most companies have dashboards that describe the past. Very few have systems that predict what happens next and recommend what to do about it. The models that exist live in notebooks. They never reach the workflows where decisions actually happen.",
      problemP2: "Vert AI builds predictive models that drive commercial action. Not charts. Not slides. Deployed scoring and recommendation systems.",
      gapsLabel: "Why dashboards fall short",
      gaps: "Descriptive analytics cannot predict outcomes|Models in notebooks never reach production|No integration with operational workflows|Scoring requires real-time or near-real-time deployment|No feedback loop between prediction and outcome",
      buildsLabel: "What we build",
      buildsTitle: "Systems we deliver in this category.",
      builds: "Churn prediction and intervention models|Customer lifetime value modeling|Lead scoring and prioritization systems|Demand and inventory forecasting|Assortment and pricing optimization support|Propensity models for marketing and sales|Customer quality and segmentation scoring|Operational classification and prioritization",
      workflowsLabel: "Typical decisions",
      workflowsTitle: "Decisions we improve.",
      workflows: "Which customers are most likely to churn|Where to allocate sales effort|What inventory levels to maintain|Which leads to prioritize|How to segment customers by value|Where to adjust pricing|Which marketing cohorts to invest in",
      humanLabel: "Decision support, not replacement",
      humanTitle: "Models inform. Humans decide.",
      humanDesc: "Every model is built with interpretability, confidence scoring, and a clear action framework. The system provides the signal. Your team applies the judgment. Decision thresholds are configurable and transparent.",
      engagementLabel: "Typical engagement",
      engagementTitle: "Delivered through Predictive AI.",
      engagementDesc: "Predictive models and decision science solutions are delivered through the Predictive AI engagement, which covers decision framing, model design, training, evaluation, deployment, and business interpretation.",
      engagementHref: "/services/predictive-ai",
      engagementCta: "Learn about Predictive AI",
      ctaHeadline: "Name the decision.",
      ctaDesc: "We will scope the model, the data requirements, and the deployment path.",
    },
    dataPlatforms: {
      label: "Solution",
      heroTitle: "Data Platforms for AI",
      hubDesc: "Source-of-truth, semantic, and QA layers required for reliable AI and consistent analytics in production.",
      heroSubtitle: "The source-of-truth, semantic, and QA layers required for reliable AI, consistent analytics, and production model deployment. Built for trust and traceability.",
      problemLabel: "The problem",
      problemTitle: "AI cannot run on untrusted data.",
      problemP1: "AI and analytics investments collapse when the data underneath is fragmented, inconsistent, or untrusted. Models trained on unreliable inputs produce unreliable decisions. Reports built on inconsistent logic erode confidence faster than they build it.",
      problemP2: "Vert AI builds the data foundation your AI and decision systems require before they can operate reliably.",
      gapsLabel: "Why existing data stacks fall short",
      gaps: "Fragmented sources with no single source of truth|Inconsistent metric definitions across teams|No semantic layer for AI model inputs|Missing data quality and trust controls|Pipeline fragility and lack of traceability",
      buildsLabel: "What we build",
      buildsTitle: "Systems we deliver in this category.",
      builds: "Source-of-truth data architecture|Semantic layer and metric definitions|Data quality and trust controls|Event instrumentation and traceability|AI and ML readiness layers|Entity resolution and identity management|Pipeline reliability engineering|Documentation and governance frameworks",
      workflowsLabel: "Common data challenges",
      workflowsTitle: "Problems we solve.",
      workflows: "Reports give different answers depending on who runs them|AI models trained on inconsistent inputs|No single source of truth across systems|Missing instrumentation for key business events|Data quality issues blocking AI deployment|Teams cannot agree on metric definitions|Pipeline failures with no traceability",
      humanLabel: "Designed for trust",
      humanTitle: "Data your team can rely on.",
      humanDesc: "Every data platform includes documentation, governance rules, quality monitoring, and clear ownership mapping. The goal is not just clean data. It is trusted data that your organization and AI stack can depend on.",
      engagementLabel: "Typical engagement",
      engagementTitle: "Delivered through Data Platforms.",
      engagementDesc: "Data platform solutions are delivered through the Data Platforms engagement, which covers source mapping, data modeling, quality controls, instrumentation, AI readiness, and documentation.",
      engagementHref: "/services/data-platforms",
      engagementCta: "Learn about Data Platforms",
      ctaHeadline: "Start with the data.",
      ctaDesc: "We will scope the architecture and the path to reliability.",
    },
  },
  authority: {
    vetVendor: {
      label: "Strategic guide",
      heroTitle: "How to Vet an AI Vendor",
      heroSubtitle: "A decision framework for operators evaluating AI implementation partners. What to look for, what to avoid, and how to separate real capability from polished marketing.",
      readTime: "12 min read · Evergreen reference",
      thesisLabel: "The core problem",
      thesisTitle: "Most AI vendor evaluations miss what matters.",
      thesisP1: "Companies evaluating AI vendors tend to focus on credentials, case studies, and pricing. These are useful but insufficient. The variables that determine whether an AI project succeeds or fails are almost entirely about engineering capability, data maturity assessment, production readiness, and post-deployment support.",
      thesisP2: "This guide provides a practical framework for separating vendors who can deliver production systems from those who deliver presentations.",
      sectionsLabel: "Evaluation framework",
      sectionsTitle: "What to assess.",
      sections: "Production track record|Technical depth vs. breadth|Data assessment capability|Deployment and handoff model|Pricing and scope transparency|Post-launch support structure",
      sectionDescs: "Ask for systems running in production today. Demos and POCs are not evidence of delivery capability.|Specialists who go deep on fewer problems outperform generalists who claim to do everything. Ask about architecture decisions, not just outcomes.|A strong vendor assesses your data readiness before committing to a timeline. Weak vendors skip this step and blame the data later.|How does the system get into production? Who maintains it? If the answer is unclear, the project will stall at deployment.|Fixed scope with defined deliverables outperforms open-ended retainers. Ask for output guarantees.|AI systems require monitoring, retraining, and refinement. Ask what happens after launch.",
      takeawaysLabel: "Key signals",
      takeawaysTitle: "What separates strong vendors from weak ones.",
      takeaways: "They scope before they sell|They assess data readiness before committing to timelines|They build for production, not for demos|They guarantee outputs, not just effort|They explain tradeoffs honestly|They have a clear post-launch model|They do not outsource core engineering",
      perspectiveLabel: "The Vert AI perspective",
      perspectiveTitle: "We built Vert AI to pass this test.",
      perspectiveDesc: "Every engagement has defined deliverables, an output guarantee, and a single point of contact from scope to production. No subcontractors. No handoffs. No ambiguity about what gets delivered.",
      relatedLabel: "Explore further",
      relatedTitle: "Related solutions and resources.",
      relatedSolutions: "What production-grade AI actually means|Agentic AI engagement|All solutions",
      relatedHrefs: "/insights/production-grade-ai|/services/agentic-ai|/solutions",
      ctaHeadline: "Evaluating AI partners?",
      ctaDesc: "Talk through your requirements. We will give you an honest assessment of fit and what a production-ready engagement looks like.",
    },
    productionGrade: {
      label: "Technical reference",
      heroTitle: "What 'Production-Grade AI' Actually Means",
      heroSubtitle: "A technical definition for operators and decision-makers. What separates a working demo from a system that runs reliably inside a business.",
      readTime: "10 min read · Evergreen reference",
      thesisLabel: "The definition gap",
      thesisTitle: "Everyone claims production-grade. Few deliver it.",
      thesisP1: "The term 'production-grade' has been diluted by overuse. Vendors use it to describe anything from a Jupyter notebook with a Streamlit front-end to a fully monitored, fault-tolerant system with operational documentation. For buyers, this ambiguity is expensive.",
      thesisP2: "This page defines what production-grade actually requires, why most AI projects fail to reach it, and what to look for in a partner who can deliver it.",
      sectionsLabel: "Production requirements",
      sectionsTitle: "What production-grade demands.",
      sections: "Reliability and fault tolerance|Monitoring and observability|Data pipeline integrity|Security and access controls|Documentation and handoff|Operational support model",
      sectionDescs: "The system handles errors gracefully, retries intelligently, and degrades without catastrophic failure. It runs on schedules or triggers, not manual execution.|Performance metrics, quality scores, drift detection, and alerting. You know when the system degrades before your users do.|Input data is validated, versioned, and traceable. The system knows when its inputs change in ways that affect output quality.|Role-based access, audit trails, and compliance-ready architecture. Not afterthoughts bolted on before launch.|Complete technical documentation, operating procedures, and team enablement. The system is transferable, not dependent on the builder.|Monitoring, retraining, refinement, and roadmap support. Production is the beginning of the system's lifecycle, not the end.",
      takeawaysLabel: "Diagnostic checklist",
      takeawaysTitle: "Is your AI system production-grade?",
      takeaways: "It runs without manual intervention|It handles errors and edge cases gracefully|It is monitored with clear alerting|Its data inputs are validated and traceable|It has complete documentation|It has defined operational ownership|It has a retraining and refinement plan|It meets security and compliance requirements",
      perspectiveLabel: "The Vert AI standard",
      perspectiveTitle: "Every system we build meets this bar.",
      perspectiveDesc: "We do not ship prototypes. Every engagement ends with a deployed, monitored, documented system with operational handoff. If we cannot deliver to production standard, we continue at zero professional fee until we do.",
      relatedLabel: "Explore further",
      relatedTitle: "Related solutions and resources.",
      relatedSolutions: "Agentic AI engagement|AI Agents and Workflow Automation|Data Platforms for AI",
      relatedHrefs: "/services/agentic-ai|/solutions/ai-agents-workflow-automation|/solutions/data-platforms-for-ai",
      ctaHeadline: "Need a production-grade system?",
      ctaDesc: "Describe the workflow. We will scope the system, the deployment path, and the production requirements.",
    },
    workflowsDeserveAI: {
      label: "Decision framework",
      heroTitle: "Which Workflows Deserve AI — and Which Don't",
      heroSubtitle: "Not every process needs AI. A practical framework for identifying where AI creates real value versus where it creates expensive complexity.",
      readTime: "9 min read · Evergreen reference",
      thesisLabel: "The selection problem",
      thesisTitle: "Most companies automate the wrong workflows first.",
      thesisP1: "The pressure to adopt AI leads companies to apply it everywhere without evaluating where it actually creates value. The result is a portfolio of expensive pilots that solve problems nobody prioritized and ignore the workflows that would generate the highest return.",
      thesisP2: "This framework helps operators identify which workflows deserve AI investment, which deserve simpler automation, and which should stay manual.",
      sectionsLabel: "Evaluation criteria",
      sectionsTitle: "How to assess workflow-AI fit.",
      sections: "Volume and repetition|Decision complexity|Data availability|Error cost and tolerance|Integration requirements|Measurement clarity",
      sectionDescs: "High-volume, repetitive workflows with consistent patterns are the strongest candidates. Low-volume, highly variable workflows rarely justify AI investment.|Workflows requiring multi-step reasoning, contextual judgment, or pattern recognition benefit from AI. Simple rule-based processes are better served by traditional automation.|AI requires structured, accessible, and sufficiently rich data. If the data does not exist or requires extensive cleanup, start with the data platform.|Workflows where errors are expensive but detectable are good candidates. Workflows where errors are catastrophic and undetectable require extreme caution.|Workflows that operate in isolation are easier to automate. Workflows deeply embedded in complex systems require more architectural planning.|If you cannot measure the improvement, you cannot justify the investment. Clear baseline metrics and success criteria are prerequisites.",
      takeawaysLabel: "Decision signals",
      takeawaysTitle: "When to invest in AI.",
      takeaways: "The workflow is high-volume and repetitive|The data exists and is reasonably structured|The error cost is manageable with human oversight|The improvement is measurable in business terms|The workflow has clear boundaries and handoff points|Standard software cannot handle the complexity|The team is ready to adopt and operate the system",
      perspectiveLabel: "How Vert AI approaches this",
      perspectiveTitle: "We start with the workflow, not the technology.",
      perspectiveDesc: "Every engagement begins with workflow selection and business framing. We help clients identify the highest-value automation targets before committing to a build. If AI is not the right answer, we say so.",
      relatedLabel: "Explore further",
      relatedTitle: "Related solutions and resources.",
      relatedSolutions: "AI Agents and Workflow Automation|Document Intelligence|Customer Support Systems|All solutions",
      relatedHrefs: "/solutions/ai-agents-workflow-automation|/solutions/document-intelligence|/solutions/customer-support-service-systems|/solutions",
      ctaHeadline: "Not sure where AI fits?",
      ctaDesc: "Describe your workflows. We will help you identify where AI creates real value and where it does not.",
    },
  },
  industriesHub: {
    heroLabel: "Industries",
    heroTitle: "AI systems built for your sector.",
    heroSubtitle: "Production-grade AI, data, and decision systems built around the workflows and constraints of your sector.",
    listLabel: "Sectors we serve",
    listTitle: "Industry-specific systems.",
    listSubtitle: "We build for industries where standard software stops short and where a failed implementation carries real cost.",
    bridgeLabel: "Cross-industry capabilities",
    bridgeTitle: "Same rigor. Different context.",
    bridgeDesc: "Every industry engagement draws from the same core solutions and delivery model. The architecture, data patterns, and operational requirements change. The engineering standard does not.",
    ctaHeadline: "Tell us about your industry.",
    ctaDesc: "We scope the system around your workflows, constraints, and data environment.",
  },
  industries: {
    accounting: {
      label: "Industry",
      heroTitle: "AI Systems for Accounting Firms",
      hubDesc: "Document processing, knowledge retrieval, and workflow automation built for the compliance, accuracy, and audit requirements of accounting practices.",
      heroSubtitle: "Production-grade AI systems for document processing, knowledge retrieval, client communication, and workflow automation. Built for the compliance, accuracy, and audit requirements of professional accounting.",
      painsLabel: "The challenge",
      painsTitle: "Where standard software stops short.",
      painsP1: "Accounting firms handle enormous volumes of structured and unstructured documents, repetitive client inquiries, and complex compliance requirements. Standard practice management software handles scheduling and billing but cannot automate the knowledge-intensive work that consumes senior staff time.",
      painsP2: "AI systems built for accounting workflows must meet accuracy, compliance, and audit-trail requirements that generic automation tools cannot satisfy.",
      painsListLabel: "Common workflow pain points",
      pains: "Manual document processing for tax returns and financial statements|Repetitive client inquiries consuming senior staff time|Knowledge trapped in partner expertise and institutional memory|Compliance checking that relies on individual knowledge|Inconsistent work product quality across team members|Seasonal capacity constraints with no scalable solution",
      systemsLabel: "What we build",
      systemsTitle: "Systems for accounting workflows.",
      systems: "Document extraction from tax forms and financial statements|Client inquiry automation with compliance-aware responses|Knowledge retrieval systems for tax code and regulatory guidance|Engagement letter and proposal automation|Work paper review and quality assurance systems|Internal knowledge assistants for staff training and reference",
      solutionsLabel: "Relevant solutions",
      solutionsTitle: "Explore by system category.",
      solutions: "Document Intelligence|Knowledge Systems and Enterprise Retrieval|AI Agents and Workflow Automation|Customer Support Systems",
      solutionHrefs: "/solutions/document-intelligence|/solutions/knowledge-systems-enterprise-retrieval|/solutions/ai-agents-workflow-automation|/solutions/customer-support-service-systems",
      engagementLabel: "Typical engagements",
      engagementTitle: "How we deliver for accounting firms.",
      engagementDesc: "Most accounting firm engagements are delivered through Agentic AI for workflow automation, Generative AI for document intelligence, or Data Platforms when the firm needs to restructure its data foundation first.",
      engagements: "Agentic AI|Data Platforms",
      engagementHrefs: "/services/agentic-ai|/services/data-platforms",
      ctaHeadline: "Describe the workflow.",
      ctaDesc: "We will scope the system around your firm's specific requirements, compliance constraints, and capacity challenges.",
    },
    manufacturing: {
      label: "Industry",
      heroTitle: "AI for Manufacturers and Distributors",
      hubDesc: "Demand forecasting, supply chain automation, and operational intelligence built for manufacturing and distribution workflows.",
      heroSubtitle: "Production-grade AI systems for demand forecasting, inventory optimization, supply chain communication, and operational decision-making. Built for the complexity and scale of manufacturing and distribution.",
      painsLabel: "The challenge",
      painsTitle: "Where standard ERP and planning tools stop short.",
      painsP1: "Manufacturers and distributors operate with complex supply chains, volatile demand patterns, and thin margins. ERP systems manage transactions but cannot predict demand shifts, optimize inventory allocation, or automate the communication workflows that connect suppliers, warehouses, and customers.",
      painsP2: "AI systems for manufacturing must integrate with existing ERP and warehouse systems, handle messy operational data, and deliver recommendations that floor managers and planners can act on.",
      painsListLabel: "Common operational pain points",
      pains: "Demand forecasting that relies on spreadsheets and intuition|Inventory allocation decisions that ignore real-time signals|Manual supplier communication and order management|Quality control that catches problems after production|Disconnected data across ERP, WMS, and planning tools|No decision support for pricing, assortment, or procurement timing",
      systemsLabel: "What we build",
      systemsTitle: "Systems for manufacturing and distribution.",
      systems: "Demand forecasting and inventory optimization models|Supplier communication and order automation|Quality prediction and defect classification|Production scheduling optimization support|Procurement timing and pricing intelligence|Operational dashboards with decision-grade data",
      solutionsLabel: "Relevant solutions",
      solutionsTitle: "Explore by system category.",
      solutions: "Predictive Models and Decision Science|AI Agents and Workflow Automation|Data Platforms for AI",
      solutionHrefs: "/solutions/predictive-models-decision-science|/solutions/ai-agents-workflow-automation|/solutions/data-platforms-for-ai",
      engagementLabel: "Typical engagements",
      engagementTitle: "How we deliver for manufacturers.",
      engagementDesc: "Manufacturing engagements typically involve Predictive AI for forecasting and optimization, Agentic AI for workflow automation, and Data Platforms when the data foundation needs work first.",
      engagements: "Predictive AI|Agentic AI|Data Platforms",
      engagementHrefs: "/services/predictive-ai|/services/agentic-ai|/services/data-platforms",
      ctaHeadline: "Describe the operation.",
      ctaDesc: "We will scope the system around your supply chain, production workflow, or data challenge.",
    },
    services: {
      label: "Industry",
      heroTitle: "AI for Service Businesses and Trades",
      hubDesc: "Workflow automation, scheduling intelligence, and client communication systems built for high-volume service operations.",
      heroSubtitle: "Production-grade AI systems for client intake, scheduling optimization, service communication, and operational automation. Built for the speed and reliability demands of service businesses.",
      painsLabel: "The challenge",
      painsTitle: "Where service management software stops short.",
      painsP1: "Service businesses and trades handle high volumes of client requests, scheduling complexity, and communication overhead. CRM and field service tools manage contacts and appointments but cannot automate the intake, triage, and communication workflows that consume dispatcher and coordinator time.",
      painsP2: "AI systems for service businesses must be fast, reliable, and simple enough for field teams to use without training.",
      painsListLabel: "Common operational pain points",
      pains: "Client intake and triage that depends on phone calls and emails|Scheduling that cannot optimize for route, skill, and priority|Repetitive client communication consuming office staff|No systematic follow-up or service quality monitoring|Knowledge trapped in senior technicians and dispatchers|Quoting and estimation that relies on individual experience",
      systemsLabel: "What we build",
      systemsTitle: "Systems for service operations.",
      systems: "Client intake automation with intelligent triage|Scheduling optimization with route and skill matching|Automated service communication and follow-up|Knowledge retrieval for field technicians|Quoting and estimation support systems|Service quality monitoring and feedback loops",
      solutionsLabel: "Relevant solutions",
      solutionsTitle: "Explore by system category.",
      solutions: "AI Agents and Workflow Automation|Customer Support Systems|Knowledge Systems and Enterprise Retrieval",
      solutionHrefs: "/solutions/ai-agents-workflow-automation|/solutions/customer-support-service-systems|/solutions/knowledge-systems-enterprise-retrieval",
      engagementLabel: "Typical engagements",
      engagementTitle: "How we deliver for service businesses.",
      engagementDesc: "Service business engagements are typically delivered through Agentic AI for workflow automation and Generative AI for customer communication knowledge.",
      engagements: "Agentic AI",
      engagementHrefs: "/services/agentic-ai",
      ctaHeadline: "Describe the service workflow.",
      ctaDesc: "We will scope the system around your intake, scheduling, or communication challenge.",
    },
    ecommerce: {
      label: "Industry",
      heroTitle: "AI for Ecommerce Operations",
      hubDesc: "Customer intelligence, catalog optimization, and profitability measurement built for DTC and retail ecommerce at scale.",
      heroSubtitle: "Production-grade AI systems for customer intelligence, catalog optimization, support automation, and profitability measurement. Built for the speed and complexity of modern ecommerce operations.",
      painsLabel: "The challenge",
      painsTitle: "Where ecommerce platforms stop short.",
      painsP1: "Ecommerce operations generate enormous data volumes across customer behavior, product catalogs, marketing channels, and fulfillment. Platform tools handle transactions and basic analytics but cannot build the customer intelligence, predictive models, and decision systems that drive profitable growth at scale.",
      painsP2: "AI systems for ecommerce must integrate with existing platforms, handle real-time data, and deliver recommendations that merchandising and marketing teams can act on immediately.",
      painsListLabel: "Common operational pain points",
      pains: "Customer acquisition costs growing with no visibility into quality by channel|Merchandising decisions based on revenue, not profit contribution|Support volume growing faster than team capacity|No predictive models for churn, LTV, or demand|Catalog optimization relying on manual curation|Attribution and measurement that contradicts across platforms",
      systemsLabel: "What we build",
      systemsTitle: "Systems for ecommerce operations.",
      systems: "Customer LTV and churn prediction models|Profitability measurement by channel, cohort, and product|Support automation with product knowledge integration|Recommendation and personalization engines|Demand forecasting and inventory optimization|Catalog enrichment and search optimization",
      solutionsLabel: "Relevant solutions",
      solutionsTitle: "Explore by system category.",
      solutions: "Predictive Models and Decision Science|Customer Support Systems|Data Platforms for AI",
      solutionHrefs: "/solutions/predictive-models-decision-science|/solutions/customer-support-service-systems|/solutions/data-platforms-for-ai",
      engagementLabel: "Typical engagements",
      engagementTitle: "How we deliver for ecommerce.",
      engagementDesc: "Ecommerce engagements typically span multiple pillars: Predictive AI for forecasting, recommendation, and profitability measurement; Agentic AI for support and catalog automation; and Data Platforms when the data foundation needs work.",
      engagements: "Predictive AI|Data Platforms|Agentic AI",
      engagementHrefs: "/services/predictive-ai|/services/data-platforms|/services/agentic-ai",
      ctaHeadline: "Describe the ecommerce challenge.",
      ctaDesc: "We will scope the system around your growth, measurement, or operations problem.",
    },
  },
  seo: {
    home: {
      title: "Vert AI — Production AI, Data & Decision Systems",
      description: "Vert AI builds production-grade AI, data platforms, and decision systems for operators who need better workflows, sharper decisions, and measurable value.",
      keywords: "production AI systems, AI systems build, data platforms for AI, decision systems, decision science consulting, AI consulting Canada, AI workflow automation, AI agents for business workflows",
    },
    services: {
      title: "AI Services — Agentic, Generative, Predictive | Vert AI",
      description: "Four production-grade AI engagements: Agentic AI, Generative AI, Predictive AI, and Data Platforms. Scoped, built, and deployed end-to-end by one Canadian team.",
      keywords: "AI services, AI engagements, agentic AI, generative AI, predictive AI, data platforms",
    },
    agenticAI: {
      title: "Agentic AI Development & Workflow Automation | Vert AI",
      description: "Bounded AI agents with routing, controls, fallback paths, and human-in-the-loop oversight. Automate high-value workflows with production-grade agentic systems.",
      keywords: "agentic AI, AI agents, workflow automation, autonomous agents, AI orchestration, human-in-the-loop",
    },
    generativeAI: {
      title: "Generative AI & Retrieval Systems for Enterprise | Vert AI",
      description: "Retrieval-grounded generative AI with citations, evaluation, and accuracy thresholds. Turn institutional knowledge into answers your teams can trust.",
      keywords: "generative AI, RAG, retrieval augmented generation, enterprise AI, knowledge systems",
    },
    predictiveAI: {
      title: "Predictive AI & ML Models for Production | Vert AI",
      description: "Production ML for churn, LTV, demand, recommendation, propensity, and profitability. Models deployed into the workflows where decisions actually happen.",
      keywords: "predictive AI, machine learning, churn prediction, LTV modeling, demand forecasting, recommendation systems",
    },
    dataPlatforms: {
      title: "Data Platforms for AI — Modern Data Architecture | Vert AI",
      description: "Source-of-truth architecture, semantic layer, quality controls, and AI-readiness instrumentation. The foundation production AI depends on.",
      keywords: "data platforms, data architecture, semantic layer, AI-ready data, data engineering Canada",
    },
    solutions: {
      title: "AI Solutions — Production Systems by Domain | Vert AI",
      description: "AI agents, document intelligence, knowledge systems, customer support automation, predictive models, and data platforms — built and deployed by one team.",
      keywords: "AI solutions, AI use cases, document intelligence, knowledge systems, predictive models",
    },
    aiAgents: {
      title: "AI Agents & Workflow Automation Systems | Vert AI",
      description: "Reduce manual throughput costs with bounded AI agents. Routing logic, controls, fallback paths, and human review at the points that matter.",
      keywords: "AI agents, workflow automation, process automation, intelligent automation",
    },
    documentIntelligence: {
      title: "Document Intelligence & Extraction Systems | Vert AI",
      description: "Production-grade document AI for contracts, invoices, forms, and unstructured PDFs. Extraction, classification, and validation with audit trails.",
      keywords: "document intelligence, document AI, OCR, intelligent document processing, IDP",
    },
    knowledgeSystems: {
      title: "Enterprise Knowledge Systems & Retrieval | Vert AI",
      description: "Retrieval-augmented assistants that reason across policies, SOPs, product data, and commercial documentation with source attribution and accuracy controls.",
      keywords: "knowledge systems, enterprise search, RAG, retrieval augmented, internal knowledge AI",
    },
    customerSupport: {
      title: "AI Customer Support & Service Systems | Vert AI",
      description: "Lift resolution speed and contain cost-per-ticket. Intent classification, knowledge-grounded responses, agent assist, and confidence-based escalation.",
      keywords: "AI customer support, customer service automation, agent assist, ticket deflection",
    },
    predictiveModels: {
      title: "Predictive Models & Decision Science Systems | Vert AI",
      description: "Production models for churn, LTV, demand, recommendation, and profitability — wired into the operational workflows where commercial decisions happen.",
      keywords: "predictive models, decision science, ML models, churn modeling, demand forecasting",
    },
    dataPlatformsForAI: {
      title: "Data Platforms for AI — Production Foundations | Vert AI",
      description: "Source-of-truth architecture, semantic layer, quality controls, and AI-readiness instrumentation. The data foundation your AI and analytics depend on.",
      keywords: "data platform, AI-ready data, modern data stack, semantic layer, data foundation",
    },
    industries: {
      title: "Industries — AI Systems by Sector | Vert AI",
      description: "Production AI for accounting firms, manufacturing & distribution, professional services, and ecommerce operations across Canada and the US.",
      keywords: "AI by industry, AI consulting industries, sector AI solutions",
    },
    accounting: {
      title: "AI for Accounting Firms — Audit Automation | Vert AI",
      description: "AI systems for accounting and audit firms: document intelligence, workpaper automation, client intake, and review workflows. Production-grade and audit-ready.",
      keywords: "AI accounting, audit automation, accounting firm AI, CPA technology",
    },
    manufacturing: {
      title: "AI for Manufacturing & Distribution Operations | Vert AI",
      description: "Demand forecasting, production planning, predictive maintenance, and supply chain optimization. AI systems built for manufacturing and distribution operators.",
      keywords: "manufacturing AI, distribution AI, demand forecasting, predictive maintenance, supply chain AI",
    },
    serviceBusinesses: {
      title: "AI for Professional Services Firms | Vert AI",
      description: "Knowledge systems, document intelligence, proposal automation, and engagement intelligence for professional services and consulting firms.",
      keywords: "AI professional services, consulting AI, services firm automation",
    },
    ecommerce: {
      title: "AI for Ecommerce Operations & Growth | Vert AI",
      description: "Recommendation, demand forecasting, profitability measurement, support automation, and catalog intelligence for ecommerce and DTC operators.",
      keywords: "ecommerce AI, DTC AI, recommendation systems, ecommerce automation",
    },
    insights: {
      title: "Insights — AI Strategy & Implementation Analysis | Vert AI",
      description: "Implementation-grade analysis on AI systems, data infrastructure, and the economics of commercial decision-making. Written for senior operators.",
      keywords: "AI insights, AI strategy, AI implementation, data engineering analysis",
    },
    vetVendor: {
      title: "How to Vet an AI Vendor — Buyer's Guide | Vert AI",
      description: "Separate real AI capability from polished marketing. The questions, criteria, and signals that distinguish production-grade vendors from prototype shops.",
      keywords: "AI vendor evaluation, AI procurement, choose AI consultant, AI buyer guide",
    },
    productionGrade: {
      title: "What 'Production-Grade AI' Actually Means | Vert AI",
      description: "What separates a demo from a system that runs in production. Engineering, monitoring, fallback logic, and operational discipline that AI requires.",
      keywords: "production AI, production grade AI, MLOps, AI deployment",
    },
    workflowsDeserveAI: {
      title: "Which Workflows Deserve AI — Selection Framework | Vert AI",
      description: "Where AI creates measurable value and where it does not. A diagnostic framework for choosing workflows that justify the engineering investment.",
      keywords: "AI use case selection, AI ROI, workflow automation criteria, AI prioritization",
    },
    about: {
      title: "About Vert AI — Production AI Consulting | Vert AI",
      description: "Vert AI is a deep-tech AI consultancy founded by Guillaume Belisle. We close the execution gap between AI strategy and production systems. Based in Canada.",
      keywords: "about Vert AI, Guillaume Belisle, AI consulting Canada, deep tech consultancy",
    },
    contact: {
      title: "Contact Vert AI — Discuss Your AI System | Vert AI",
      description: "Talk to Vert AI about your AI, data, or decision-system opportunity. Direct contact with the team that will scope, build, and deploy it.",
      keywords: "contact Vert AI, AI consulting contact, AI engagement",
    },
    notFound: {
      title: "Page Not Found | Vert AI",
      description: "The page you are looking for does not exist or has moved.",
    },
    unsubscribe: {
      title: "Unsubscribe | Vert AI",
      description: "Manage your email preferences.",
    },
  },
};

